import api from "../api";

// ---------------------------------------------------------------------------
// permissionsApi.js
//
// FE wrapper for /api/Permissions/*. The full EffectiveAccessBundle is
// returned by /api/Auth/login (`response.data.access`) AND by
// /api/Permissions/me. Both shapes are identical.
//
// Cache: sessionStorage["permissions"]
//   • Written by the login thunk (preferred — saves a round-trip).
//   • Also written by refresh() below.
//   • Read by usePermissions() and useAuth().
//
// Phase 3 (Legacy Access Parity):
//   The cached value is now the FULL bundle object (identity + role +
//   permissions[] + every isXyz flag + modules map). Older builds wrote
//   only a permissions string ARRAY here; this module tolerates both
//   shapes (readCached returns the array as-is if that's what's there).
//   Code that wants the new flags should call useAuth() which handles
//   the legacy fallback internally.
// ---------------------------------------------------------------------------

const STORAGE_KEY = "permissions";

export function readCached() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function writeCached(bundle) {
  try {
    if (bundle == null) sessionStorage.removeItem(STORAGE_KEY);
    else sessionStorage.setItem(STORAGE_KEY, JSON.stringify(bundle));
  } catch {
    // sessionStorage full / disabled — silent no-op
  }
}

export function clearCached() {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch { /* no-op */ }
}

/** Fetch the full EffectiveAccessBundle for the current user and cache it. */
export async function refresh() {
  const res = await api.get("Permissions/me");
  const bundle = res?.data ?? null;
  writeCached(bundle);
  return bundle;
}

/** Admin/debug: bundle for any user. Requires CanAccessAdmin server-side. */
export async function getEffective(empId) {
  if (!empId) throw new Error("getEffective: empId is required");
  const res = await api.get(`Permissions/effective/${encodeURIComponent(empId)}`);
  return res?.data ?? null;
}

/** Return the cached bundle if present, otherwise fetch + cache. */
export async function getOrLoad() {
  const cached = readCached();
  // Older builds stored only the permission array here. That's not a
  // usable bundle — treat it as a cache miss and refetch.
  if (cached && !Array.isArray(cached) && typeof cached === "object") {
    return cached;
  }
  return await refresh();
}

export default {
  refresh,
  getEffective,
  readCached,
  writeCached,
  clearCached,
  getOrLoad,
};
